import { protectedResources } from '@/authConfig';
import makeGetRequestAsync, {
  makePostRequestAsync,
  makePUTRequestAsync
} from '@/http/Helpers/httpRequestHelpers';

import {
  ApproveStagedUpdateLineResult,
  ErpAttemptPayload,
  ErpCategoryConfigItem,
  LastApprovedSnapshot,
  PagedSupplierUpdateRecords,
  PendingApprovalRow,
  RejectStagedUpdateLineResult,
  ResendErpAttemptResult,
  SetSupplierUpdateClientSettingsCommand,
  SetSupplierUpdateClientSettingsResult,
  StagedSupplierUpdateDetail,
  SupplierUpdateAuditPage,
  SupplierUpdateClientSettings,
  SupplierUpdateHistorySubmission,
  UpdateErpCategoryConfigCommand
} from '../types/erpSync.types';

const apiScopes = protectedResources.integrationGatewayApi.scopes;
const baseUrl = protectedResources.integrationGatewayApi.endpoint;

/**
 * Reproduces the URL transform of the generated `$integrationGatewayApi` fetch client
 * (src/http/OpenAPI/clients/react-query/integration-gateway.ts): controller routes keep their
 * `/api/...` prefix, and on non-localhost hosts the first `/api/` segment of the combined
 * pathname is stripped to match the deployed gateway routing.
 */
const buildUrl = (path: string) => {
  try {
    const url = new URL(`${baseUrl}${path}`);
    if (url.hostname !== 'localhost' && url.hostname !== '127.0.0.1') {
      url.pathname = url.pathname.replace(/\/api\//, '');
    }
    return url.toString();
  } catch {
    // Non-absolute base (e.g. unset env under tests) - keep the raw concatenation.
    return `${baseUrl}${path}`;
  }
};

export const erpSyncApi = {
  queries: {
    // TODO: migrate to $integrationGatewayApi.useQuery('get', '/api/integration-gateway/erp-category-config') once OpenAPI types are regenerated
    getErpCategoryConfig: async () => {
      const response = await makeGetRequestAsync<{ value: ErpCategoryConfigItem[] }>(
        apiScopes,
        buildUrl('/api/integration-gateway/erp-category-config')
      );
      return response.data.value;
    },

    // TODO: migrate to $integrationGatewayApi.useQuery('get', '/api/integration-gateway/supplier-update-client-settings') once OpenAPI types are regenerated
    getSupplierUpdateClientSettings: async () => {
      const response = await makeGetRequestAsync<{ value: SupplierUpdateClientSettings }>(
        apiScopes,
        buildUrl('/api/integration-gateway/supplier-update-client-settings')
      );
      return response.data.value;
    },

    // TODO: migrate to $integrationGatewayApi.useQuery('get', '/api/integration-gateway/pending-approvals') once OpenAPI types are regenerated
    getPendingApprovals: async () => {
      const response = await makeGetRequestAsync<{ value: PendingApprovalRow[] }>(
        apiScopes,
        buildUrl('/api/integration-gateway/pending-approvals')
      );
      return response.data.value;
    },

    // TODO: migrate to $integrationGatewayApi.useQuery('get', '/api/integration-gateway/staged-supplier-updates/{id}') once OpenAPI types are regenerated
    getStagedSupplierUpdateDetail: async (id: string) => {
      const response = await makeGetRequestAsync<{ value: StagedSupplierUpdateDetail }>(
        apiScopes,
        buildUrl(`/api/integration-gateway/staged-supplier-updates/${id}`)
      );
      return response.data.value;
    },

    // TODO: migrate to $integrationGatewayApi.useQuery('get', '/api/integration-gateway/last-approved-snapshots') once OpenAPI types are regenerated
    getLastApprovedSnapshots: async (supplierCompanyId: number) => {
      const response = await makeGetRequestAsync<{ value: LastApprovedSnapshot[] }>(
        apiScopes,
        buildUrl(
          `/api/integration-gateway/last-approved-snapshots?supplierCompanyId=${supplierCompanyId}`
        )
      );
      return response.data.value;
    },

    // TODO: migrate to $integrationGatewayApi.useQuery('get', '/api/integration-gateway/supplier-update-audit') once OpenAPI types are regenerated
    getSupplierUpdateAudit: async (
      supplierCompanyId: number,
      page: number,
      pageSize: number
    ) => {
      const response = await makeGetRequestAsync<{ value: SupplierUpdateAuditPage }>(
        apiScopes,
        buildUrl(
          `/api/integration-gateway/supplier-update-audit?supplierCompanyId=${supplierCompanyId}&page=${page}&pageSize=${pageSize}`
        )
      );
      return response.data.value;
    },

    // TODO: migrate to $integrationGatewayApi.useQuery('get', '/api/integration-gateway/supplier-update-records') once OpenAPI types are regenerated
    // Feature #7210 / S14 (REQ-081..084): server-side search/filter/sort/paging. `search` and
    // `outstandingSection` are omitted from the query string entirely when unset (rather than sent
    // empty) — the backend treats a present-but-blank param the same as absent, but omitting it keeps
    // the request URL legible and matches the other paged endpoints' convention in this file.
    getSupplierUpdateRecords: async (
      params: {
        search?: string;
        outstandingSection?: string;
        page?: number;
        pageSize?: number;
      } = {}
    ) => {
      const query = new URLSearchParams();
      if (params.search) {
        query.set('search', params.search);
      }
      if (params.outstandingSection) {
        query.set('outstandingSection', params.outstandingSection);
      }
      query.set('page', String(params.page ?? 1));
      query.set('pageSize', String(params.pageSize ?? 25));

      const response = await makeGetRequestAsync<{ value: PagedSupplierUpdateRecords }>(
        apiScopes,
        buildUrl(`/api/integration-gateway/supplier-update-records?${query.toString()}`)
      );
      return response.data.value;
    },

    // TODO: migrate to $integrationGatewayApi.useQuery('get', '/api/integration-gateway/supplier-update-history/{supplierCompanyId}') once OpenAPI types are regenerated
    getSupplierUpdateHistory: async (supplierCompanyId: number) => {
      const response = await makeGetRequestAsync<{
        value: SupplierUpdateHistorySubmission[];
      }>(
        apiScopes,
        buildUrl(`/api/integration-gateway/supplier-update-history/${supplierCompanyId}`)
      );
      return response.data.value;
    },

    // TODO: migrate to $integrationGatewayApi.useQuery('get', '/api/integration-gateway/staged-supplier-updates/{id}/lines/{lineId}/erp-attempts/{attemptId}/payload') once OpenAPI types are regenerated
    // Feature #7210 / S12 (REQ-073): the ONLY call in this file that ever returns the sensitive
    // outbound JSON payload — never cached alongside the history list, always fetched on demand by
    // `useDownloadErpAttemptPayload` at the moment the user clicks Download.
    getErpAttemptPayload: async (
      stagedSupplierUpdateId: string,
      lineId: string,
      attemptId: string
    ) => {
      const response = await makeGetRequestAsync<{ value: ErpAttemptPayload }>(
        apiScopes,
        buildUrl(
          `/api/integration-gateway/staged-supplier-updates/${stagedSupplierUpdateId}/lines/${lineId}/erp-attempts/${attemptId}/payload`
        )
      );
      return response.data.value;
    }
  },
  mutations: {
    // TODO: migrate to $integrationGatewayApi.useMutation('put', '/api/integration-gateway/erp-category-config') once OpenAPI types are regenerated
    updateErpCategoryConfig: async (command: UpdateErpCategoryConfigCommand) => {
      const response = await makePUTRequestAsync<
        { value: ErpCategoryConfigItem[] },
        UpdateErpCategoryConfigCommand
      >(apiScopes, buildUrl('/api/integration-gateway/erp-category-config'), command);
      return response.data.value;
    },

    // TODO: migrate to $integrationGatewayApi.useMutation('put', '/api/integration-gateway/supplier-update-client-settings') once OpenAPI types are regenerated
    // Master per-client approvals switch (PBI #6085). Admin-only (AccessPlatformConfiguration) — the
    // endpoint enforces it too; returns the enable-time approver-readiness warnings.
    setSupplierUpdateClientSettings: async (
      command: SetSupplierUpdateClientSettingsCommand
    ) => {
      const response = await makePUTRequestAsync<
        { value: SetSupplierUpdateClientSettingsResult },
        SetSupplierUpdateClientSettingsCommand
      >(
        apiScopes,
        buildUrl('/api/integration-gateway/supplier-update-client-settings'),
        command
      );
      return response.data.value;
    },

    // TODO: migrate to $integrationGatewayApi.useMutation('post', '/api/integration-gateway/staged-supplier-updates/{id}/lines/{lineId}/approve') once OpenAPI types are regenerated
    // NOTE: passes `{}` rather than no body at all — `makePostRequestAsync`'s "no data" branch
    // swallows the error (logs then resolves undefined instead of rethrowing), which would
    // silently hide 400/403/404/409 failures from the mutation's onError toast.
    approveStagedUpdateLine: async (stagedSupplierUpdateId: string, lineId: string) => {
      const response = await makePostRequestAsync<
        { value: ApproveStagedUpdateLineResult },
        Record<string, never>
      >(
        apiScopes,
        buildUrl(
          `/api/integration-gateway/staged-supplier-updates/${stagedSupplierUpdateId}/lines/${lineId}/approve`
        ),
        {}
      );
      return response.data.value;
    },

    // TODO: migrate to $integrationGatewayApi.useMutation('post', '/api/integration-gateway/staged-supplier-updates/{id}/lines/{lineId}/reject') once OpenAPI types are regenerated
    // The command binds the reason from the body only (route already carries both ids) — the ids
    // are still included in the body defensively in case a future contract change starts reading them.
    rejectStagedUpdateLine: async (
      stagedSupplierUpdateId: string,
      lineId: string,
      reason: string
    ) => {
      const response = await makePostRequestAsync<
        { value: RejectStagedUpdateLineResult },
        { reason: string; stagedSupplierUpdateId: string; lineId: string }
      >(
        apiScopes,
        buildUrl(
          `/api/integration-gateway/staged-supplier-updates/${stagedSupplierUpdateId}/lines/${lineId}/reject`
        ),
        { reason, stagedSupplierUpdateId, lineId }
      );
      return response.data.value;
    },

    // TODO: migrate to $integrationGatewayApi.useMutation('post', '/api/integration-gateway/staged-supplier-updates/{id}/lines/{lineId}/resend') once OpenAPI types are regenerated
    // Feature #7210 / S12 (US-14, REQ-070/071/072/073). Same "pass `{}`, never no body" note as
    // approveStagedUpdateLine above applies here.
    resendErpAttempt: async (stagedSupplierUpdateId: string, lineId: string) => {
      const response = await makePostRequestAsync<
        { value: ResendErpAttemptResult },
        Record<string, never>
      >(
        apiScopes,
        buildUrl(
          `/api/integration-gateway/staged-supplier-updates/${stagedSupplierUpdateId}/lines/${lineId}/resend`
        ),
        {}
      );
      return response.data.value;
    }
  }
};
